<template>
  <v-card
    :width="hoveringNavCard ? '15rem' : '3.7rem'"
    @mouseover="hoveringNavCard = true"
    @mouseleave="hoveringNavCard = false"
    :class="{
      'card-light': !isDarkTheme,
      'card-dark': isDarkTheme,
      'floating-nav-card': true
    }"
    flat
  >
    <v-list v-if="authStore.userData">
      <!-- Top navigation list -->
      <v-list-item v-for="tab in navTabs" :key="tab" :to="tab.to" nav color="primary">
        <template #prepend>
          <v-icon>
            {{ tab.icon }}
          </v-icon>
        </template>
        <v-list-item-title class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t(`navigationOptions.${tab.title}`) }}
        </v-list-item-title>
      </v-list-item>
      <v-divider class="mt-7" />
      <v-list-subheader>
        <span class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t('navigationOptions.accounts') }}
        </span>
      </v-list-subheader>

      <!-- Search Dialog -->
      <search-dialog key="navbar-search-dialog" :hoveringNavCard="hoveringNavCard" />

      <!-- Workspace Navigation -->
      <v-list-item
        @click="$emit('toggle-workspace-nav')"
        :active="$route.path.includes('/workspaces')"
        :variant="$route.path.includes('/workspaces') ? 'tonal' : undefined"
        color="primary"
        nav
      >
        <template #prepend>
          <v-icon>mdi-view-quilt-outline</v-icon>
        </template>
        <v-list-item-title class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t('navigationOptions.lists') }}
        </v-list-item-title>
        <template #append> <v-icon size="small"> mdi-chevron-right</v-icon></template>
      </v-list-item>

      <!-- my territory -->
      <v-list-item to="/myterritory" color="primary" nav>
        <template #prepend>
          <v-icon>mdi-domain</v-icon>
        </template>
        <v-list-item-title class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t('navigationOptions.myAccounts') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider class="mt-7" />

      <v-list-subheader>
        <span class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t('navigationOptions.admin') }}
        </span>
      </v-list-subheader>

      <!-- add accounts -->
      <v-list-item to="/upload" color="primary" nav>
        <template #prepend>
          <v-icon>mdi-plus-circle-outline</v-icon>
        </template>
        <v-list-item-title class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t('navigationOptions.addAccounts') }}
        </v-list-item-title>
      </v-list-item>

      <!-- Support Tools - Only visible for support@syftai.com -->
      <v-list-item
        v-if="isSupportUser || authStore.impersonationActive"
        to="/support-impersonation"
        color="primary"
        nav
        class="mb-10"
      >
        <template #prepend>
          <v-icon size="small">mdi-account-convert</v-icon>
        </template>
        <v-list-item-title class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t('navigationOptions.supportTools') }}
        </v-list-item-title>
      </v-list-item>
      <v-list-item v-if="isManagerOrAdmin" to="/reporting" color="primary" nav>
        <template #prepend>
          <v-icon>mdi-chart-box</v-icon>
        </template>
        <v-list-item-title class="appearing-text" :class="{ show: hoveringNavCard }">
          {{ $t('navigationOptions.reporting') }}
        </v-list-item-title>
      </v-list-item>

      <user-menu :hoveringNavCard="hoveringNavCard" />
    </v-list>
  </v-card>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/store/auth'
import UserMenu from '@/components/nav/UserMenu.vue'
import SearchDialog from '@/components/search/SearchDialog.vue'
import { SUPPORT_EMAIL } from '@/config/constants'
import { useTheme } from 'vuetify'

export default {
  name: 'NavigationCard',
  components: {
    UserMenu,
    SearchDialog
  },
  setup() {
    const theme = useTheme()
    return { theme }
  },
  emits: ['toggle-workspace-nav'],
  data() {
    return {
      hoveringNavCard: false,
      navTabs: [
        { title: 'value', to: '/', icon: 'mdi-shimmer', displayBadge: false },
        {
          title: 'actions',
          to: '/actions',
          icon: 'mdi-lightning-bolt-outline',
          displayBadge: false
        }
      ]
    }
  },
  computed: {
    ...mapStores(useAuthStore),

    // Check if the current user is the support user
    isSupportUser() {
      // Ensure both user and userData are loaded to prevent race conditions
      return (
        this.authStore.user?.email === SUPPORT_EMAIL &&
        this.authStore.userData?.email === SUPPORT_EMAIL
      )
    },

    isManagerOrAdmin() {
      return this.authStore.userIsManager || this.authStore.userData?.is_admin
    },

    // Check if the current theme is dark
    isDarkTheme() {
      return this.theme.name.value === 'dark'
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/styles/cards.scss' as *;

.floating-nav-card {
  position: fixed;
  top: -4rem;
  bottom: 2.5rem;
  left: 1rem;
  margin: auto;
  height: fit-content;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 1000;

  transition:
    height 0.2s ease-in-out,
    width 0.2s ease-in-out;

  &.card-light {
    @include card-light;
  }

  &.card-dark {
    @include card-dark;
  }
}

.appearing-text {
  display: none;
  opacity: 0;

  &.show {
    display: block;
    animation: fadeIn 0.2s ease-in-out 0.1s forwards;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.text-expand-vertical {
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  visibility: hidden;
  transition:
    max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &.show {
    max-height: 500px; /* Adjust as needed for content */
    opacity: 1;
    visibility: visible;
    transition:
      max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
</style>
